import React, { useCallback } from "react";
import classnames from "classnames";
import { isAfter } from "date-fns";
import { observer } from "mobx-react";

import { combineFilters } from "@formant/core/common/combineFilters";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import { IBaseEvent } from "@formant/core/model/IBaseEvent";
import { IDevice } from "@formant/core/model/IDevice";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IFilter } from "@formant/core/model/IFilter";
import { IShare } from "@formant/core/model/IShare";
import { Fader } from "@formant/ui/component/common/Fader";

import { useStores } from "../../../context/StoreContext";
import {
    seekFilterExcludedStreams,
    SeekOption,
    seekOptionToNoun,
} from "../../../store/SeekOption";
import { formatDateString } from "../../../utils/formatDateString";
import { Button } from "../../common/Button";
import { CircleButton } from "../../common/CircleButton";
import { Clock } from "../../common/Clock";
import { Collapsible } from "../../common/Collapsible";
import { SeekButton } from "../SeekButton";
import styles from "./index.scss";
import { PlaybackSpeedSelect } from "./PlaybackSpeedSelect";
import { Scrubber } from "./Scrubber";
import { SeekOptionSelect } from "./SeekOptionSelect";
import { TimescaleSelect } from "./TimescaleSelect";

export interface ITimelineProps<T extends IBaseEvent> {
    context: IFilter;
    filter?: IFilter;
    focusedStream?: [string, AddableModuleType?];
    share?: IShare;
    onSeek: (time: Date) => void;
    editingEvent?: T;
    selectedEvent?: IEvent;
    onChangeEditingEvent?: (editingEvent: T) => void;
    onSelectEvent: (event: IEventOrAggregate) => void;
    device?: IDevice;
    seekOption: SeekOption;
    analyticsOnly?: boolean;
    onChangeSeekOption: (seekOption: SeekOption) => void;
}

export const Timeline = observer(function Timeline<T extends IBaseEvent>(
    props: ITimelineProps<T>,
) {
    const {
        context,
        focusedStream,
        share,
        onSeek,
        editingEvent,
        selectedEvent,
        onChangeEditingEvent,
        onSelectEvent,
        device,
        onChangeSeekOption,
        analyticsOnly,
    } = props;

    const { observeStore } = useStores();
    const { time, mode, seekOption } = observeStore;

    const onToggleLive = useCallback(() => {
        if (observeStore.mode === "live") {
            observeStore.mode = "pause";
        } else {
            observeStore.mode = "live";
            observeStore.speed = 1;
        }
    }, [observeStore]);

    const onTogglePlay = useCallback(() => {
        observeStore.toggleMode();
    }, [observeStore]);

    const [focusedStreamName] = focusedStream || [];
    const filter = focusedStreamName
        ? combineFilters({ names: [focusedStreamName] }, props.filter)
        : props.filter;

    const seekFilter = combineFilters(
        combineFilters(context, filter),
        seekFilterExcludedStreams,
    );

    const scopeEnd = share?.scope?.end;
    const nowOutsideScope =
        scopeEnd && isAfter(new Date(), parseIsoDate(scopeEnd));

    const seekNoun = seekOptionToNoun(seekOption);

    const center = (
        <>
            <SeekButton
                direction="previous"
                filter={seekFilter}
                className={classnames(styles["seek-previous"], styles.button)}
                onSeek={onSeek}
                onSelectEvent={onSelectEvent}
                seekOption={seekOption}
                label={`Seek to previous ${seekNoun}`}
                hotkeyCode="shift+left"
            />
            {mode === "pause" ? (
                <CircleButton
                    className={classnames(styles.play, styles.button)}
                    onClicked={onTogglePlay}
                    icon="play"
                />
            ) : (
                <CircleButton
                    className={classnames(styles.play, styles.button)}
                    onClicked={onTogglePlay}
                    icon="pause"
                />
            )}
            <SeekButton
                direction="next"
                filter={seekFilter}
                className={classnames(styles["seek-next"], styles.button)}
                onSeek={onSeek}
                onSelectEvent={onSelectEvent}
                seekOption={seekOption}
                label={`Seek to next ${seekNoun}`}
                hotkeyCode="shift+right"
            />
        </>
    );

    return (
        <div className={styles.timeline}>
            {!analyticsOnly && (
                <Scrubber
                    context={context}
                    filter={filter}
                    share={share}
                    onSelectEvent={onSelectEvent}
                    device={device}
                    // Visualize either the edting event or the selected event
                    selectedEvent={editingEvent || (selectedEvent as T)}
                    // But only the editing event may be changed
                    onChangeSelectedEvent={
                        editingEvent ? onChangeEditingEvent : undefined
                    }
                    seekOption={seekOption}
                />
            )}

            <div className={styles.content}>
                <div className={styles.left}>
                    <TimescaleSelect analyticsOnly={analyticsOnly ?? false} />
                    {!analyticsOnly && <PlaybackSpeedSelect />}
                    {!analyticsOnly && !share && (
                        <SeekOptionSelect
                            filter={seekFilter}
                            seekOption={seekOption}
                            onChange={onChangeSeekOption}
                            className={classnames(
                                styles["seek-option"],
                                styles.button,
                            )}
                        />
                    )}
                </div>
                <div className={styles.center}>{!analyticsOnly && center}</div>
                <div className={styles.right}>
                    <Collapsible
                        bottom={true}
                        right={true}
                        icon="clock"
                        buttonText={formatDateString(time, "HH:mm:ss")}
                        buttonTooltip="Set date and time"
                        className={styles.button}
                    >
                        <Clock
                            time={time}
                            dateClassName={styles.date}
                            timeClassName={styles.time}
                        />
                    </Collapsible>
                    {!analyticsOnly && (
                        <Fader visible={!nowOutsideScope}>
                            <Button
                                onClicked={onToggleLive}
                                className={classnames(
                                    styles.live,
                                    styles.button,
                                    mode === "live" && styles.active,
                                )}
                                data-testid="live-button"
                            >
                                Live
                            </Button>
                        </Fader>
                    )}
                </div>
            </div>
        </div>
    );
});
